import { ChangeEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Input } from 'components/Input/Input'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { RightCell } from 'components/HonestTable/HonestTable.styled'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText } from 'styling/Typography'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { OPS_DAY_REPORTS } from 'pages/Operations/Shared/opsAnalyticsData'
import { IOpsDayReportRow } from 'types/opsAnalytics.interface'
import { formatNumbersForDisplay } from 'utils/helpers'
import {
  AvailabilityText,
  FilterRow,
  Footnote,
  LockGlyph,
  ReportCell,
  ReportId,
  SearchWrap,
  TonnesPct,
} from './DayReports.styled'

const tonnesTone = (item: IOpsDayReportRow): 'success' | 'warning' | 'danger' => {
  const deficit = (item.target - item.tonnes) / item.target
  if (deficit > 0.15) return 'danger'
  if (deficit > 0.05) return 'warning'
  return 'success'
}

const availabilityTone = (pct: number): 'success' | 'neutral' | 'danger' => {
  if (pct < 80) return 'danger'
  if (pct >= 95) return 'success'
  return 'neutral'
}

const columns: IHonestTableColumn<IOpsDayReportRow>[] = [
  {
    id: 'report',
    header: 'Report',
    minWidth: '10rem',
    cell: (item) => (
      <ReportCell>
        <ReportId>#{item.id}</ReportId>
        <BodyText weight='600'>{item.dateLabel}</BodyText>
      </ReportCell>
    ),
  },
  {
    id: 'shift',
    header: 'Shift',
    minWidth: '8rem',
    cell: (item) => <BodyText>{item.shiftName}</BodyText>,
  },
  {
    id: 'tonnes',
    header: 'Tonnes',
    minWidth: '12rem',
    cell: (item) => (
      <Flex center gap='0.5rem'>
        <BodyText>
          {formatNumbersForDisplay(item.tonnes)} / {formatNumbersForDisplay(item.target)}
        </BodyText>
        <TonnesPct $tone={tonnesTone(item)}>
          {Math.round((item.tonnes / item.target) * 100)}%
        </TonnesPct>
      </Flex>
    ),
  },
  {
    id: 'availability',
    header: 'Availability',
    minWidth: '8rem',
    cell: (item) => (
      <AvailabilityText $tone={availabilityTone(item.availabilityPct)}>
        {item.availabilityPct}%
      </AvailabilityText>
    ),
  },
  {
    id: 'incidents',
    header: 'Incidents',
    minWidth: '8rem',
    cell: (item) =>
      item.incidents > 0 ? (
        <StatusPill status='danger'>{item.incidents}</StatusPill>
      ) : (
        <OtherText grey>0</OtherText>
      ),
  },
  {
    id: 'spacer',
    header: '',
    fill: true,
    cell: () => null,
  },
  {
    id: 'status',
    header: 'Status',
    align: 'right',
    minWidth: '12rem',
    cell: (item) => (
      <RightCell>
        {item.locked ? (
          <StatusPill status='neutral'>
            <LockGlyph>
              <svg width='10' height='10' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                <rect x='5' y='11' width='14' height='9' rx='2' fill='currentColor' />
                <path d='M8 11V8a4 4 0 0 1 8 0v3' stroke='currentColor' strokeWidth='2.5' fill='none' />
              </svg>
            </LockGlyph>
            Locked
          </StatusPill>
        ) : (
          <StatusPill status='info'>Open — locks at shift end</StatusPill>
        )}
      </RightCell>
    ),
  },
]

export const DayReports = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  const reports = OPS_DAY_REPORTS.filter(
    (item) => !query || item.dateLabel.toLowerCase().includes(query),
  )

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1>Day reports</Head1>
          <DemoBadge />
          <Button variant='black' size='sm' onClick={() => window.print()}>
            Print / PDF
          </Button>
        </Flex>
        <OtherText grey>The immutable shift archive — what happened on any given day.</OtherText>
        {USE_OPS_MOCKS ? (
          <>
            <FilterRow>
              <SearchWrap>
                <Input
                  nomarg
                  search
                  noinvalidtext
                  placeholder='Search by date…'
                  value={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                />
              </SearchWrap>
            </FilterRow>
            <HonestTable
              columns={columns}
              data={reports}
              keyField='id'
              onClick={() => navigate('/operations/analytics/shift-review')}
              emptyMessage='No reports match.'
            />
            <Footnote>Per-day packs open here once shift aggregates are stored.</Footnote>
          </>
        ) : (
          <NoData text='Reports file automatically at every shift close once connected.' />
        )}
      </PageWrapper>
    </>
  )
}
